/**
 * physics.ts
 * Physics constants for Starfall Courier's fixed-timestep game loop.
 *
 * All gravity, jump, friction, and movement tuning values live here.
 * Never hardcode these inline in engine or entity files — import from here.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1 and ADR-002.
 *
 * @module physics
 */

// DECISION: constants re-exported rather than computed from gameConstants.ts
// so physics.ts is the single authoritative file for tuning — callers only
// need one import for all physics values.

/** Downward acceleration applied every physics tick (px/s²). */
export const GRAVITY = 1800;

/** Velocity applied on jump press (px/s, negative = upward). */
export const JUMP_FORCE = -620;

/**
 * Velocity applied on a mid-air double jump (px/s, negative = upward).
 * Equal to {@link JUMP_FORCE} as the initial Phase 4 tuning — keeps the
 * second jump feeling identical to the ground jump so the new ability
 * is unambiguous. Tunable independently here when Phase 5 polish wants
 * a weaker / different-feeling air jump.
 * Gated by `nova.canDoubleJump`. The flag itself is defined in `SaveData.abilities`
 * but is not yet wired into runtime init — the World 3 unlock event will set it
 * (deferred ROADMAP item). Until then this constant is dormant in gameplay.
 */
export const AIR_JUMP_FORCE = -620;

/**
 * Reduced upward force applied when the jump button is released early,
 * enabling variable jump height.
 * Applied as a velocity clamp — if Nova's Y velocity is more negative than
 * this value when jump is released, it is clamped to this value.
 */
export const JUMP_RELEASE_CLAMP = -200;

/** Terminal downward velocity cap (px/s). Prevents tunnelling on fast falls. */
export const MAX_FALL_SPEED = 900;

/**
 * Horizontal deceleration applied when no directional input is held (px/s²).
 * Higher = snappier stops; lower = slippery feel.
 */
export const GROUND_FRICTION = 1200;

/**
 * Horizontal acceleration applied when a direction is held (px/s²).
 * Separate from friction so ramp-up and ramp-down feel can be tuned
 * independently.
 */
export const GROUND_ACCELERATION = 1400;

/** Maximum horizontal run speed (px/s). */
export const MAX_RUN_SPEED = 240;

/**
 * Fixed physics timestep in seconds.
 * The game loop accumulates real elapsed time and steps physics in this
 * increment — required for 120Hz ProMotion correctness (ADR-002).
 * Must match FIXED_DT in gameConstants.ts.
 */
export const FIXED_STEP = 1 / 60;

/**
 * Ledge forgiveness — horizontal overlap correction applied when Nova's
 * corner clips a platform edge by this many pixels or fewer (px).
 * Prevents "catching" on the lip of a platform during a running jump.
 */
export const LEDGE_FORGIVENESS = 4;

/**
 * Downward velocity applied when Nova activates a stomp dive (px/s).
 * Overrides current vy if it is lower, giving a consistent dive speed.
 */
export const STOMP_FALL_SPEED = 600;

/**
 * Upward velocity applied when Nova lands from an active stomp dive (px/s, negative).
 * Produces a small bounce — roughly 50 % of a full jump height — that can be
 * extended into a full jump by holding the jump button on the same frame.
 */
export const STOMP_BOUNCE = -300;

/**
 * Horizontal velocity applied to Nova on enemy contact damage (px/s).
 * Pushes Nova away from the enemy. Always applied as `abs(KNOCKBACK_VX)`
 * in the direction away from the enemy.
 */
export const KNOCKBACK_VX = 200;

/**
 * Upward velocity applied to Nova on enemy contact damage (px/s, negative).
 * Gives a small upward hop so Nova visibly recoils.
 */
export const KNOCKBACK_VY = -250;

/**
 * Downward acceleration applied to VFX particles (px/s²).
 *
 * Deliberately much lighter than `GRAVITY` — particles are decorative sparks
 * and dust, and full world gravity makes them drop out of frame before the
 * burst reads. Lives here rather than inline in `particleSystem.ts` because
 * AGENTS.md requires every physics constant to be declared in this module.
 */
export const PARTICLE_GRAVITY = 200;
